import type { MenuDataItem } from '@ant-design/pro-components';

/**
 * 侧栏菜单的展开态与滚动位置保持。
 *
 * 背景：ProLayout 的 BaseMenu 会在路径变化时用 `matchMenuKeys` 覆盖 openKeys，
 * 用户手动展开的其他分组会在跳页后消失；若再按 pathname 给菜单容器换 key，
 * 连滚动位置都会一起重置。这里用「受控 openKeys + 记住滚动」来避免这两种重置。
 */

/**
 * 计算跳转后应当展开的分组 key。
 *
 * - 当前路由链上的分组必须展开（保证能看到高亮项）；
 * - 用户本页手动展开过的其他分组保留，除非它显式收起过；
 * - 全部展开时，保留用户手动收起的分组。
 *
 * 菜单是两级结构，key 为分组 key，不含叶子项。
 */
export const resolveOpenKeys = (input: {
  /** 当前路由对应的分组链（由 getNavigationTrail 推出） */
  matchedKeys: string[];
  /** 当前已展开的分组 */
  openKeys: string[];
  /** 用户在本次会话里手动展开过的分组 */
  manualOpen: string[];
  /** 用户在本次会话里手动收起过的分组 */
  manualClose: string[];
  /** 菜单是否默认全部展开 */
  defaultOpenAll: boolean;
  /** 全部可展开的分组 key */
  allGroupKeys: string[];
  /** 子分组 → 父分组，用于保证子分组不会在父级收起时孤立展开 */
  parents?: Record<string, string>;
}): string[] => {
  const {
    matchedKeys,
    openKeys,
    manualOpen,
    manualClose,
    defaultOpenAll,
    allGroupKeys,
    parents = {},
  } = input;
  const closed = new Set(manualClose);
  const base = defaultOpenAll
    ? allGroupKeys.filter((key) => !closed.has(key))
    : openKeys.filter((key) => allGroupKeys.includes(key) && !closed.has(key));
  const merged = new Set<string>(base);
  for (const key of manualOpen) {
    if (!closed.has(key) && allGroupKeys.includes(key)) merged.add(key);
  }
  // 当前路由所在分组必须展开，优先级高于手动收起
  for (const key of matchedKeys) merged.add(key);
  // 去掉父级未展开的孤立子分组，避免出现「子项可见但父项收起」的错位
  let changed = true;
  while (changed) {
    changed = false;
    for (const key of [...merged]) {
      const parent = parents[key];
      if (parent && !merged.has(parent)) {
        merged.delete(key);
        changed = true;
      }
    }
  }
  return allGroupKeys.filter((key) => merged.has(key));
};

/** 从菜单数据里取出所有可作为分组展开的 key（有 children 的节点） */
export const collectGroupKeys = (menus: MenuDataItem[]): string[] => {
  const keys: string[] = [];
  const walk = (items: MenuDataItem[]) => {
    for (const item of items) {
      if (item.key === undefined || item.key === null) continue;
      if (item.children?.length) {
        keys.push(String(item.key));
        walk(item.children);
      }
    }
  };
  walk(menus);
  return keys;
};

/** 把路径变化前记录的滚动位置写回容器，超出范围时自动收敛到 0 */
export const restoreScrollTop = (
  container: HTMLElement | null | undefined,
  top: number,
) => {
  if (!container) return;
  const max = Math.max(0, container.scrollHeight - container.clientHeight);
  container.scrollTop = Math.min(Math.max(0, top), max);
};
